import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PendingEmployees } from '@treams/treams-analytics';
import { useSendCycleMomentManagerRemindersMutation } from '@treams/treams-network';
import { GraphQLErrorMessage, Label, Modal, Paragraph, Popover, TextArea } from '@treams/treams-ui';

export interface Manager {
	id: string;
	name: string;
}

export function RemindManagersModal({
	close,
	cycleTemplateID,
	momentID,
	momentName,
	managers,
	findPendingEmployees,
}: {
	close: () => void;
	cycleTemplateID: string;
	momentID: string;
	momentName: string;
	managers: Manager[];
	findPendingEmployees: (managerID: string, momentID: string) => PendingEmployees;
}) {
	const { t } = useTranslation();
	const [message, setMessage] = useState('');

	const [send, { loading, error }] = useSendCycleMomentManagerRemindersMutation();

	const onSave = async () => {
		try {
			await send({
				variables: {
					input: {
						cycleTemplateID,
						momentTemplateID: momentID,
						managers: managers.map((manager) => ({
							managerID: manager.id,
							pendingEmployeeNames: findPendingEmployees(manager.id, momentID).pendingEmployeeNames,
						})),
						text: message || undefined,
					},
				},
			});
			close();
		} catch {
			// Error is surfaced via the `error` returned from the mutation hook.
		}
	};

	const isSingleManager = managers.length === 1;

	return (
		<Popover center visible onDismiss={close}>
			<Modal>
				<Modal.Header title={t('analytics.cycles.managers.remind.modalTitle')} onClose={close} />
				<Modal.Contents>
					<Paragraph mb={2}>
						{isSingleManager ? (
							<Trans
								i18nKey="analytics.cycles.managers.remind.confirmationOne"
								values={{ name: managers[0].name }}
								components={{ strong: <strong /> }}
							/>
						) : (
							<Trans
								i18nKey="analytics.cycles.managers.remind.confirmationMany"
								values={{ count: managers.length }}
								components={{ strong: <strong /> }}
							/>
						)}
					</Paragraph>
					<Paragraph mb={3}>
						{isSingleManager ? (
							<Trans
								i18nKey="analytics.cycles.managers.remind.descriptionOne"
								values={{ momentName }}
								components={{ strong: <strong /> }}
							/>
						) : (
							<Trans
								i18nKey="analytics.cycles.managers.remind.descriptionMany"
								values={{ momentName }}
								components={{ strong: <strong /> }}
							/>
						)}
					</Paragraph>
					<Label mb={2}>{t('analytics.cycles.managers.remind.messageLabel')}</Label>
					<TextArea
						autoFocus
						minRows={4}
						value={message}
						onChange={setMessage}
						placeholder={t('analytics.cycles.managers.remind.placeholder')}
					/>
					{error && <GraphQLErrorMessage error={error} />}
				</Modal.Contents>
				<Modal.FormFooter
					onCancel={close}
					saveTitle={t('analytics.cycles.managers.remind.send')}
					disabled={loading}
					onSave={onSave}
				/>
			</Modal>
		</Popover>
	);
}
